// Firebase App Check (v3.143.0) — Bot-/Missbrauchsschutz auf App-Ebene.
//
// App Check beweist dem Backend, dass eine Anfrage aus DIESER Web-App kommt und nicht
// aus einem Skript oder einer fremden Seite. Ergänzt das Rate Limiting: Das begrenzt,
// WIE VIEL ein angemeldeter Nutzer darf; App Check begrenzt, WER überhaupt fragen darf.
//
// ── Bewusst im Beobachtungs-Modus ausgeliefert ───────────────────────────────
// Ohne `REACT_APP_RECAPTCHA_SITE_KEY` ist diese Datei ein vollständiger No-op — die App
// verhält sich exakt wie vorher, und das SDK wird nicht einmal geladen. Das ist Absicht:
// Eine falsch konfigurierte App-Check-Durchsetzung sperrt ALLE Nutzer aus. Der Weg zur
// Scharfschaltung ist deshalb dreistufig und jederzeit umkehrbar:
//
//   1. Firebase Console → App Check → Web-App registrieren (reCAPTCHA v3), Site Key als
//      REACT_APP_RECAPTCHA_SITE_KEY setzen (GitHub Secret + .env.local).
//      → Ab hier schicken echte Clients Tokens mit; das Backend protokolliert nur.
//   2. Im Console-Dashboard beobachten, bis „verifizierte Anfragen" stabil ~100 % sind.
//   3. Erst dann APPCHECK_ENFORCE=true in functions/.env setzen — ab da blockt das
//      Backend Anfragen ohne gültiges Token.
//
// Schritt 2 ist nicht optional: PWA-Nutzer mit altem Cache und lange offene Tabs
// schicken noch eine Weile Anfragen ohne Token.
//
// ── Warum dynamischer Import ────────────────────────────────────────────────
// `firebase/app-check` bringt den reCAPTCHA-Provider mit. Ein statischer Import in
// index.js zöge ihn ins Haupt-Bundle — auch für alle, die App Check gar nicht nutzen
// (heute: alle). Der dynamische Import lädt ihn nur, wenn ein Site Key gesetzt ist.
// Gleiche Erwägung wie beim Code-Splitting in v3.98.0.

import app from './config';

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Einmalig aufgelöstes Promise auf die Instanz (oder null). Kein erneuter Versuch bei
// Fehlschlag — sonst lüde jeder Aufruf das SDK neu nach.
let initPromise = null;

function ensureAppCheck() {
  if (!SITE_KEY) return Promise.resolve(null);
  if (initPromise) return initPromise;

  initPromise = import('firebase/app-check')
    .then(({ initializeAppCheck, ReCaptchaV3Provider }) =>
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(SITE_KEY),
        // Token läuft nach ~1 h ab; ohne Auto-Refresh bräche ein langer Arbeitstag ab.
        isTokenAutoRefreshEnabled: true,
      }),
    )
    .catch((err) => {
      // Niemals die App am Start scheitern lassen — ohne App Check läuft alles weiter,
      // nur eben ungeschützt (der Zustand von vor v3.143.0).
      console.warn('[AppCheck] Initialisierung fehlgeschlagen:', err?.message);
      return null;
    });

  return initPromise;
}

// Liefert den Header für die /api/*-Aufrufe — leeres Objekt, solange App Check nicht
// konfiguriert ist, damit die Aufrufstellen bedingungslos spreaden können:
//   headers: { ...bisherige, ...(await appCheckHeaders()) }
export async function appCheckHeaders() {
  const instance = await ensureAppCheck();
  if (!instance) return {};
  try {
    const { getToken } = await import('firebase/app-check');
    const { token } = await getToken(instance, /* forceRefresh */ false);
    return token ? { 'X-Firebase-AppCheck': token } : {};
  } catch (err) {
    // Ein fehlendes Token darf die Anfrage nicht verhindern, solange nicht erzwungen
    // wird — das Backend entscheidet, ob es das akzeptiert.
    console.warn('[AppCheck] Token konnte nicht geholt werden:', err?.message);
    return {};
  }
}

export const isAppCheckConfigured = Boolean(SITE_KEY);
export { ensureAppCheck };
